/** @format */

import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import React, { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	Alert,
	Box,
	DialogActions,
	Button,
	Typography,
	Avatar,
	Grid,
	Snackbar,
	Divider,
	Paper,
} from "@mui/material";
import {
	createContribution,
	payMissedContributions,
} from "../../../redux/actions/contributionAction";
import {
	LoadingSpinner,
	NotificationModal,
} from "../../allComponents/AllComponents";
import { buttonColors, colors } from "../../../utility/types/constant";
import { trackPurchase } from "../../../utility/redditPixel";
import { ContributionStatus } from "../../../utility/types/contributionType";
import { calculateTotalCharge } from "../../../utility/stripeFeeCalculator";
import { formatCurrency } from "../../../utility/currencyUtils";
import {
	calculateMissedContributionFees,
	LATE_FEE_PERCENTAGE,
} from "../../../utility/lateFeeCalculator";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface ContributionModalProps {
	open: boolean;
	handleClose: () => void;
	unionId: string;
	memberId: string;
	contributionPeriod: string;
	contributionAmount: number;
	currency?: string; // ISO 4217 currency code — defaults to "usd"
	isMissedContribution?: boolean; // Flag to indicate if this is a missed contribution payment
	missedDates?: string[]; // Array of missed contribution dates (YYYY-MM-DD)
	isContributionDayToday?: boolean; // Whether today is a scheduled contribution day
	isTodayAlreadyPaid?: boolean; // Whether today's contribution has already been paid
	onContributionSuccess?: () => void;
}

const ContributionModal: React.FC<ContributionModalProps> = ({
	open,
	handleClose,
	unionId,
	memberId,
	contributionPeriod,
	contributionAmount,
	currency = "usd",
	isMissedContribution = false,
	missedDates = [],
	isContributionDayToday = false,
	isTodayAlreadyPaid = false,
	onContributionSuccess,
}) => {
	const stripe = useStripe();
	const elements = useElements();
	const dispatch = useDispatch<AppDispatch>();

	// const [amount, setAmount] = useState<number>(0);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	// 🔒 Track successful contribution to disable button and prevent double submissions
	const [contributionSuccess, setContributionSuccess] = useState(false);
	const [openSuccessContributionModal, setOpenSuccessContributionModal] =
		useState(false);
	const [alert, setAlert] = useState<{
		type: "success" | "error";
		message: string;
	} | null>(null);
	const [openAlert, setOpenAlert] = useState(false);
	// const [successContribution, setSuccessContribution] = useState(false);

	// 🔒 Reset contributionSuccess state when modal opens/closes
	useEffect(() => {
		if (open) {
			setContributionSuccess(false);
			setError(null);
		}
	}, [open]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!stripe || !elements) return;

		setLoading(true);

		const cardElements = elements.getElement(CardElement);

		if (!cardElements) {
			setError("Card element not found");
			setAlert({
				type: "error",
				message:
					"Card details are incomplete. Please provide full card details.",
			});
			setOpenAlert(true);
			setLoading(false);
			handleClose();
			return;
		}

		// create payment method
		const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
			type: "card",
			card: cardElements,
		});

		if (pmError || !paymentMethod) {
			setError(pmError?.message || "Failed to create payment method");
			setAlert({
				type: "error",
				message: pmError?.message || "Failed to create payment method",
			});
			setOpenAlert(true);
			setLoading(false);
			handleClose();
			return;
		}

		try {
			let response;

			if (isMissedContribution && missedDates.length > 0) {
				// Use the missed contributions endpoint for bulk payments
				const missedPayload = {
					unionId,
					memberId,
					paymentMethodId: paymentMethod.id,
					missedDates,
				};
				response = await dispatch(
					payMissedContributions({ payload: missedPayload }),
				).unwrap();

				// Check if missed contributions were paid successfully
				if (response.success) {
					// 🔒 Mark as successful to disable button immediately
					setContributionSuccess(true);
					onContributionSuccess && onContributionSuccess();
					setOpenSuccessContributionModal(true);
					setTimeout(() => {
						setOpenSuccessContributionModal(false);
						handleClose();
					}, 6000);
				} else {
					setAlert({
						type: "error",
						message:
							"Contribution Failed. Please check your payment method and try again.",
					});
					setOpenAlert(true);
					handleClose();
				}
			} else {
				// Regular single contribution
				const payload = {
					unionId,
					memberId,
					contributionAmount,
					contributionPeriod,
					paymentMethodId: paymentMethod.id,
				};
				response = await dispatch(createContribution({ payload })).unwrap();

				if (response._id && response.status === ContributionStatus.Completed) {
					// 🔒 Mark as successful to disable button immediately
					trackPurchase(response._id);
					setContributionSuccess(true);
					onContributionSuccess && onContributionSuccess();
					setOpenSuccessContributionModal(true);
					setTimeout(() => {
						setOpenSuccessContributionModal(false);
						handleClose();
					}, 10000);
				} else {
					setAlert({
						type: "error",
						message:
							"Contribution Failed. Please check your payment method and try again.",
					});
					setOpenAlert(true);
					handleClose();
				}
			}

			// handleClose();
		} catch (error: any) {
			setError(
				error?.message ||
				"Contribution Failed. Please check your payment method and try again.",
			);
			setAlert({
				type: "error",
				message:
					error?.message ||
					"Contribution Failed. Please check your payment method and try again.",
			});
			setOpenAlert(true);
			handleClose();
		} finally {
			setLoading(false);
		}
	};

	const handleCloseSuccessContributionModal = () => {
		setOpenSuccessContributionModal(false);
	};

	const handleCloseAlert = () => {
		setOpenAlert(false);
	};

	// Calculate fee breakdown using memoization for performance
	const feeBreakdown = useMemo(() => {
		return calculateTotalCharge(contributionAmount, currency);
	}, [contributionAmount, currency]);

	// Calculate missed contribution fees including late fees
	const missedFeeBreakdown = useMemo(() => {
		if (isMissedContribution && missedDates.length > 0) {
			return calculateMissedContributionFees(contributionAmount, missedDates, undefined, currency);
		}
		return null;
	}, [isMissedContribution, missedDates, contributionAmount, currency]);

	return (
		<>
			<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
				<DialogTitle>
					{isMissedContribution ?
						"Pay Missed Contributions"
						: "Make Your Contribution"}
				</DialogTitle>
				<DialogContent>
					{error && <Alert severity="error">{error}</Alert>}
					{isMissedContribution && (
						<Alert severity="warning" sx={{ mb: 2 }}>
							You are paying for missed contributions. This payment will cover
							all your unpaid contribution dates.
						</Alert>
					)}
					{/* Today is contribution day notice - show only when today is a contribution day and not already paid */}
					{isMissedContribution &&
						isContributionDayToday &&
						!isTodayAlreadyPaid && (
							<Alert severity="info" sx={{ mb: 2 }}>
								<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
									📌 Today (
									{new Date().toLocaleDateString("en-US", {
										weekday: "long",
										month: "short",
										day: "numeric",
									})}
									) is also a contribution day.
								</Typography>
								<Typography variant="body2">
									After paying your missed contributions, you'll need to make{" "}
									<strong>today's contribution separately</strong> to be fully
									caught up.
								</Typography>
							</Alert>
						)}
					{/* Today already paid notice */}
					{isMissedContribution &&
						isContributionDayToday &&
						isTodayAlreadyPaid && (
							<Alert severity="success" sx={{ mb: 2 }}>
								<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
									✅ Today (
									{new Date().toLocaleDateString("en-US", {
										weekday: "long",
										month: "short",
										day: "numeric",
									})}
									) is already paid!
								</Typography>
								<Typography variant="body2">
									After paying your missed contributions, you'll be fully caught
									up.
								</Typography>
							</Alert>
						)}
					<Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
						{/* 💰 Fee Breakdown Section */}
						<Paper
							elevation={0}
							sx={{
								p: 2,
								mb: 3,
								backgroundColor: "#f8fafc",
								border: "1px solid #e2e8f0",
								borderRadius: 2,
							}}>
							<Typography
								variant="subtitle1"
								sx={{ fontWeight: 600, mb: 2, color: "#334155" }}>
								{isMissedContribution ?
									"Payment Summary"
									: "Contribution Summary"}
							</Typography>

							{/* Contribution Amount */}
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									mb: 1,
								}}>
								<Typography variant="body2" sx={{ color: "#64748b" }}>
									{isMissedContribution && missedFeeBreakdown ?
										`Contributions (${missedFeeBreakdown.totalDates} date${missedFeeBreakdown.totalDates > 1 ? "s" : ""} × ${formatCurrency(contributionAmount)})`
										: "Circle Contribution"}
								</Typography>
								<Typography variant="body2" sx={{ fontWeight: 500 }}>
									{isMissedContribution && missedFeeBreakdown ?
										formatCurrency(missedFeeBreakdown.contributionsSubtotal)
										: formatCurrency(feeBreakdown.netAmount)}
								</Typography>
							</Box>

							{/* Late Fees - Only show for missed contributions with late dates */}
							{isMissedContribution && missedFeeBreakdown?.hasLateFees && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										mb: 1,
										backgroundColor: "#fef3c7",
										borderRadius: 1,
										px: 1,
										py: 0.5,
										mx: -1,
									}}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<WarningAmberIcon sx={{ fontSize: 16, color: "#d97706" }} />
										<Typography variant="body2" sx={{ color: "#92400e" }}>
											Late Fee ({missedFeeBreakdown.lateDatesCount} date
											{missedFeeBreakdown.lateDatesCount > 1 ? "s" : ""} ×{" "}
											{(LATE_FEE_PERCENTAGE * 100).toFixed(1)}%)
										</Typography>
										<InfoOutlinedIcon
											sx={{
												fontSize: 14,
												color: "#d97706",
												cursor: "help",
											}}
											titleAccess="A 2.5% late fee applies to contributions paid more than 24 hours after their scheduled date."
										/>
									</Box>
									<Typography
										variant="body2"
										sx={{ fontWeight: 500, color: "#92400e" }}>
										+{formatCurrency(missedFeeBreakdown.totalLateFees)}
									</Typography>
								</Box>
							)}

							{/* Processing Fee */}
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									mb: 1,
								}}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									<Typography variant="body2" sx={{ color: "#64748b" }}>
										Processing Fee (2.9% + $0.30)
									</Typography>
									<InfoOutlinedIcon
										sx={{
											fontSize: 14,
											color: "#94a3b8",
											cursor: "help",
										}}
										titleAccess="This fee covers card payment processing costs."
									/>
								</Box>
								<Typography variant="body2" sx={{ fontWeight: 500 }}>
									{isMissedContribution && missedFeeBreakdown ?
										formatCurrency(missedFeeBreakdown.stripeFee)
										: formatCurrency(feeBreakdown.stripeFee)}
								</Typography>
							</Box>

							<Divider sx={{ my: 1.5 }} />

							{/* Total */}
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
								}}>
								<Typography
									variant="body1"
									sx={{ fontWeight: 600, color: "#1e293b" }}>
									Total to be charged
								</Typography>
								<Typography
									variant="body1"
									sx={{
										fontWeight: 700,
										color:
											isMissedContribution ? "#dc2626" : (
												buttonColors.infoDarkBlue
											),
									}}>
									{isMissedContribution && missedFeeBreakdown ?
										formatCurrency(missedFeeBreakdown.grandTotal)
										: formatCurrency(feeBreakdown.totalCharge)}
								</Typography>
							</Box>
						</Paper>

						{/* Late Fee Explanation - Only show when there are late fees */}
						{isMissedContribution && missedFeeBreakdown?.hasLateFees && (
							<Alert
								severity="warning"
								icon={<WarningAmberIcon fontSize="small" />}
								sx={{
									mb: 2,
									backgroundColor: "#fef3c7",
									"& .MuiAlert-icon": { color: "#d97706" },
								}}>
								<Typography variant="body2" sx={{ color: "#92400e" }}>
									<strong>About Late Fees:</strong> A 2.5% late fee applies to {" "}
									{missedFeeBreakdown.lateDatesCount} contribution
									{missedFeeBreakdown.lateDatesCount > 1 ? "s" : ""} that
									{missedFeeBreakdown.lateDatesCount > 1 ? " are" : " is"} more
									than 24 hours past{" "}
									{missedFeeBreakdown.lateDatesCount > 1 ? "their" : "its"}{" "}
									scheduled date. This helps maintain circle reliability.
								</Typography>
							</Alert>
						)}

						{/* Transparency Note */}
						<Alert
							severity="info"
							icon={<InfoOutlinedIcon fontSize="small" />}
							sx={{
								mb: 3,
								backgroundColor: "#eff6ff",
								"& .MuiAlert-icon": { color: "#3b82f6" },
							}}>
							{isMissedContribution && missedFeeBreakdown ?
								<Typography variant="body2" sx={{ color: "#1e40af" }}>
									<strong>
										Your full{" "}
										{formatCurrency(missedFeeBreakdown.subtotalBeforeStripeFee)}{" "}
										owed amount
									</strong>{" "}
									is collected in full. The processing fee covers card payment
									costs only.
								</Typography>
								: <Typography variant="body2" sx={{ color: "#1e40af" }}>
									<strong>
										100% of your {formatCurrency(contributionAmount)}{" "}
										contribution
									</strong>{" "}
									goes directly to your circle's pool. The processing fee covers
									card payment costs only.
								</Typography>
							}
						</Alert>

						{/* Card Element */}
						<Box sx={{ mb: 3 }}>
							<CardElement
								options={{
									style: {
										base: {
											fontSize: "16px",
											color: "#424770",

											"::placeholder": { color: "#aab7c4" },
										},
										invalid: { color: "#9e2146" },
									},
								}}
							/>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleClose}
						disabled={loading}
						sx={{
							color: colors.persianRed,
						}}>
						Cancel
					</Button>
					<Button
						type="submit"
						onClick={handleSubmit}
						variant="contained"
						disabled={loading || !stripe || contributionSuccess}
						sx={{
							color: colors.white,
							backgroundColor:
								isMissedContribution ? "#dc2626" : buttonColors.infoDarkBlue,
							"&:hover": {
								backgroundColor:
									isMissedContribution ? "#b91c1c" : buttonColors.infoDarkBlue,
							},
						}}>
						{loading ?
							<LoadingSpinner loading={loading} color={colors.white} />
							: contributionSuccess ?
								"✓ Contribution Successful"
								: isMissedContribution && missedFeeBreakdown ?
									`Pay ${formatCurrency(missedFeeBreakdown.grandTotal)}`
									: `Contribute ${formatCurrency(feeBreakdown.totalCharge)}`}
					</Button>
				</DialogActions>
			</Dialog>
			{openSuccessContributionModal && (
				<NotificationModal
					open={openSuccessContributionModal}
					handleClose={handleCloseSuccessContributionModal}>
					<Grid container spacing={2} direction="row" alignItems="center">
						<Grid item>
							<Avatar
								src="/assets/icons/congrats-icon.gif"
								sx={{ width: 30, height: 30 }}
								alt="Congrats Icon"
							/>
						</Grid>
						<Grid item xs>
							<Typography
								variant="body2"
								sx={{
									fontSize: "1.5rem",
									color: colors.darkGrey,
									display: "flex",
									alignItems: "center",
									gap: "8px",
									whiteSpace: "nowrap",
								}}>
								Thank you! Your Contribution Was Successful
							</Typography>
						</Grid>
					</Grid>
				</NotificationModal>
			)}

			<Snackbar
				open={openAlert}
				autoHideDuration={90000}
				onClose={handleCloseAlert}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				sx={{
					top: "50% !important",
					transform: "translateY(-50%)",
				}}>
				<Alert
					onClose={handleCloseAlert}
					variant="filled"
					severity={alert?.type || "success"}
					sx={{
						width: "100%",
						minHeight: "100px",
						fontSize: "1.4rem",
						padding: "20px",
						display: "flex",
						alignItems: "center",
					}}>
					{alert?.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default ContributionModal;
